<template>
  <div
    class="notification-row"
    :class="{ 'notification-row--new': !item.seenAt }"
    role="button"
    tabindex="0"
    @click="$emit('open', item)"
    @keydown.enter.prevent="$emit('open', item)"
    @keydown.space.prevent="$emit('open', item)"
  >
    <span class="notification-row__icon">
      <v-icon size="19">{{ icon }}</v-icon>
    </span>

    <div class="notification-row__body">
      <div class="notification-row__headline">
        <span v-if="!item.seenAt" class="notification-row__dot" aria-hidden="true" />
        <strong>{{ headline }}</strong>
      </div>
      <p v-if="contextLine">{{ contextLine }}</p>
      <span class="notification-row__time">{{ timeLabel }}</span>
    </div>

    <v-btn
      icon="mdi-close"
      variant="text"
      size="x-small"
      :loading="busy"
      :aria-label="$t('ncrm.notifications.deleteOne')"
      @click.stop="$emit('dismiss', item)"
    />
    <v-icon size="18" class="notification-row__chevron">mdi-chevron-right</v-icon>
  </div>
</template>

<script>
import {
  notificationAgeMeta,
  notificationHeadline,
  notificationIcon,
} from "@/Utile/ncrmNotifications.mjs";

export default {
  name: "NcrmNotificationRow",
  props: {
    item: { type: Object, required: true },
    busy: { type: Boolean, default: false },
  },
  emits: ["open", "dismiss"],
  computed: {
    icon() {
      return notificationIcon(this.item.eventType);
    },
    headline() {
      return notificationHeadline(this.item, (key, params) => this.$t(key, params));
    },
    contextLine() {
      const entity = String(this.item.payload?.entity_name || "").trim();
      const dueAt = this.item.payload?.due_at;
      if (entity && dueAt) {
        return this.$t("ncrm.notifications.entityDueContext", {
          entity,
          due: this.formatDateTime(dueAt),
        });
      }
      if (entity) return entity;
      if (dueAt) {
        return this.$t("ncrm.notifications.dueContext", {
          due: this.formatDateTime(dueAt),
        });
      }
      return "";
    },
    timeLabel() {
      const meta = notificationAgeMeta(this.item.createdAt);
      if (meta.kind === "now") return this.$t("ncrm.notifications.time.now");
      if (meta.kind === "minutes") {
        return this.$t("ncrm.notifications.time.minutesAgo", { count: meta.value });
      }
      if (meta.kind === "hours") {
        return this.$t("ncrm.notifications.time.hoursAgo", { count: meta.value });
      }
      if (meta.kind === "date") return this.formatDateTime(meta.value);
      return "";
    },
  },
  methods: {
    formatDateTime(value) {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "";
      const pad = (part) => String(part).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}, ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.notification-row {
  position: relative;
  min-height: 76px;
  margin: 0 10px 4px;
  padding: 12px 10px;
  border-radius: var(--app-radius-base);
  display: grid;
  grid-template-columns: 38px minmax(0, 1fr) 30px 18px;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}

.notification-row:hover,
.notification-row:focus-visible {
  background: rgba(var(--v-theme-primary), 0.045);
  border-color: rgba(var(--v-theme-primary), 0.14);
  outline: none;
}

.notification-row:focus-visible {
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.16);
}

.notification-row--new {
  background: rgba(var(--v-theme-tertiary), 0.06);
}

.notification-row__icon {
  width: 36px;
  height: 36px;
  border-radius: var(--app-radius-base);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.08);
}

.notification-row__body {
  min-width: 0;
}

.notification-row__headline {
  display: flex;
  align-items: flex-start;
  gap: 7px;
}

.notification-row__headline strong {
  color: var(--app-text-strong);
  font-size: 0.87rem;
  line-height: 1.35;
}

.notification-row__dot {
  width: 7px;
  height: 7px;
  margin-top: 5px;
  border-radius: 999px;
  background: rgb(var(--v-theme-tertiary));
  flex-shrink: 0;
}

.notification-row p {
  margin: 4px 0 0;
  color: var(--app-text-muted);
  font-size: 0.8rem;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.notification-row__time {
  display: inline-block;
  margin-top: 4px;
  color: var(--app-text-muted);
  opacity: 0.72;
  font-size: 0.73rem;
}

.notification-row__chevron {
  color: var(--app-text-muted);
  opacity: 0.45;
}
</style>
